import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import {
  BucketedVariation,
  RuleError,
  BucketingError,
  ExperienceChange,
} from "@convertcom/js-sdk";
import useUser from "@modules/user/hooks/useUser";
import { VariationChangeType } from "@convertcom/js-sdk-enums";
import { logError } from "@common/helpers/logger";
import useConvert from "./useConvert";
import { ConvertExperienceType } from "../types/ConvertExperienceType";

export type ExperienceResult<T = unknown> = {
  variationKey: string;
  data: T | null;
  trackConversion: (
    goalKey: string,
    ruleData?: Record<string, unknown>
  ) => void;
};

const useConvertExperience = <T = unknown>(
  experience: ConvertExperienceType
): ExperienceResult<T> => {
  const convert = useConvert();
  const { user } = useUser();
  const router = useRouter();

  const [experienceResult, setExperienceResult] = useState<ExperienceResult<T>>(
    {
      variationKey: "",
      data: null,
      trackConversion: () => undefined,
    }
  );

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    convert
      ?.onReady()
      .then(() => {
        const userContext = convert.createContext(user.id, {
          country: "CA",
          language: router.locale ?? "en",
        });

        if (userContext != null && experience != null && user.id) {
          const variation: BucketedVariation | RuleError | BucketingError =
            userContext.runExperience(experience.key, {
              locationProperties: experience.locationProperties,
              visitorProperties: experience.visitorProperties,
              updateVisitorProperties: experience.updateVisitorProperties,
            });

          if (variation) {
            const bucketedVariation = variation as BucketedVariation;
            const changesData = bucketedVariation?.changes?.find(
              ({ type }) => type === VariationChangeType.FULLSTACK_FEATURE
            );

            const { data } = (changesData as ExperienceChange) || {};

            setExperienceResult({
              variationKey: bucketedVariation.key ?? "",
              data: data?.variables_data || null,
              trackConversion: (
                goalKey: string,
                ruleData?: Record<string, unknown>
              ) => {
                userContext.trackConversion(goalKey, { ruleData });
              },
            });
          }
        }
      })
      .catch((error) => {
        // Only throw exceptions
        // https://docs.sentry.io/platforms/javascript/troubleshooting/#events-with-non-error-exception
        if (error instanceof Error) throw error;

        // Log the plain error object
        logError(`Non-error exception from Convert: ${JSON.stringify(error)}`);
      });
  }, [convert, experience, router.locale, user]);

  return experienceResult;
};

export default useConvertExperience;
